import type { HTMLAttributes } from "react";
import { cn } from "@/lib/cn";

interface AvatarProps extends HTMLAttributes<HTMLDivElement> {
  src?: string | null;
  alt?: string;
  name?: string | null;
  size?: "sm" | "md" | "lg" | "xl";
}

const SIZE_CLASSES = {
  sm: "size-7 text-xs",
  md: "size-9 text-sm",
  lg: "size-12 text-base",
  xl: "size-16 text-lg",
} as const;

export function Avatar({ src, alt, name, size = "md", className, ...props }: AvatarProps) {
  const initials = getInitials(name ?? alt ?? "?");

  return (
    <div
      role="img"
      aria-label={alt ?? name ?? undefined}
      className={cn(
        "inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full",
        "bg-brand-muted/60 font-display font-semibold text-brand",
        SIZE_CLASSES[size],
        className,
      )}
      {...props}
    >
      {src ? (
        <img src={src} alt={alt ?? name ?? ""} className="size-full object-cover" />
      ) : (
        <span>{initials}</span>
      )}
    </div>
  );
}

function getInitials(value: string): string {
  return (
    value
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? "")
      .join("") || "?"
  );
}
